import { useState, type ReactNode } from 'react';
import type {
  AttestResult,
  FactResolutionSummary,
  OpenItemsSummaryView,
  RecordFactTier,
  RecordFactView,
  ReviewCoverageReadView,
  SourceWithdrawalView,
} from '../api.js';
import {
  closureCandidateNote,
  findingChips,
  isRegisterFinding,
  protectedCandidateOf,
  type RegisterFindingValue,
} from '../register-finding.js';
import { timestamp } from '../format.js';
import { ReviewCoverage } from './ReviewCoverage.js';
import { usePrintExpanded } from './usePrintExpanded.js';

/**
 * What this record CLAIMS, and how strongly — the provisional-then-confirmed panel (ADR-0044).
 *
 * Chad Garrett's ruling of 2026-08-13 tied two things together: a restriction fact (the affordable
 * unit count the architect stated at a drawing-review meeting) and a drawing-register finding are the
 * SAME mechanism. So they render in one list, and the difference between them shows up where the
 * ruling put it — an owned item carries a person and a date, housekeeping carries neither.
 *
 * ## ⚠️ THE TIER IS THE POINT, so it is the first thing on the row
 *
 * A record that reports "53 affordable units" and a record that reports "the architect SAID 53 at a
 * meeting, and the next drawing issue will settle it" are different records. The second is the one
 * this table exists to keep. Rendering the value without its tier would collapse the distinction the
 * whole mechanism was built to hold — the claim would quietly harden into a fact, which is precisely
 * what `expiresAt` exists to prevent.
 *
 * ## ⚠️ `source` IS NOT THE RECORDER
 *
 * "Stated by the architect" and "recorded by Dana Owner" are two different people and two different
 * kinds of authority. Showing only one would either lend our authority to an outside claim or hide who
 * put it on the record. Both are shown, labelled.
 */

/** The tier in the reader's words, with the sentence that says what it actually means. */
const TIER_COPY: Record<RecordFactTier, { label: string; tone: string }> = {
  stated: { label: 'Stated', tone: 'stated' },
  disclosed: { label: 'Disclosed', tone: 'disclosed' },
  confirmed: { label: 'Confirmed', tone: 'confirmed' },
};

const KIND_LABELS: Record<string, string> = {
  restriction: 'Restriction',
  register_finding: 'Register finding',
  /*
    The tracking log's stated figures. Labelled "log figure" rather than just the number's name so a
    reader can see WHERE the claim came from without opening it — a unit count from a hand-maintained
    spreadsheet and one from a proforma are the same number carrying very different weight, and the
    tier beside it (`stated`) is only meaningful if the source is legible.

    ⚠️ The fallback (`KIND_LABELS[f.kind] ?? f.kind`) already renders an unknown kind, so this is
    legibility rather than reachability — the panel was never going to hide it.
  */
  tracking_log_figure: 'Log figure',
  /** A project's Open Items Register row (ADR-0073). */
  open_item: 'Open item',
  /** A pinned deal input — financing program or LIHTC (ADR-0069 N27). */
  deal_fact: 'Deal fact',
};

/**
 * Render the claim itself. A count renders as a number, a band or schedule as its JSON.
 *
 * ⚠️ Deliberately NOT clever. The `kind` owns the shape, and a renderer that guesses at structure
 * would silently mis-format the first shape nobody anticipated — showing a wrong-looking value beside
 * a governance tier is worse than showing a plain one.
 *
 * 🔴 THE ONE EXCEPTION IS HANDLED BEFORE THIS IS CALLED. A register finding is an object whose fields
 * are paragraphs, and stringifying it produced the walls of `{"type":"CONTRADICTION",…}` that made a
 * project page unreadable. `FindingBody` renders those; this stays the honest fallback for everything
 * else, which is what it was always for.
 */
function factValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

/**
 * A register finding, as a person would write it.
 *
 * The `summary` is a finished human sentence and becomes the headline. The `detail` and the citations
 * are reference material — real, kept, and **collapsed**, because a record with twenty-one findings is
 * something you scan before it is something you read. `<details>` rather than React state so it works
 * without JavaScript, keyboard-toggles for free, and survives a re-render with its own open/closed.
 */
function FindingBody({ finding }: { finding: RegisterFindingValue }) {
  // 🔴 FORCED OPEN IN PRINT, and a guard caught its absence before this shipped. A project record
  // printed for an IC packet must not drop the evidence behind a chevron — Chad's first UAT pass found
  // exactly that on the report's category breakdowns: six chevrons and several blank pages. It cannot
  // be a stylesheet rule; see `usePrintExpanded`.
  const printRef = usePrintExpanded();
  const chips = findingChips(finding);
  const citations = finding.citations ?? [];
  const hasMore = (finding.detail ?? '').trim() !== '' || citations.length > 0;

  return (
    <div className="finding">
      {chips.length > 0 && (
        <p className="finding__chips">
          {chips.map((c) => (
            // `title` carries the register's OWN code. Canon translates machinery notation at the
            // surface and keeps the ruled term in the record — this does both on one element.
            <span
              key={`${c.kind}-${c.code}`}
              className={`finding__chip finding__chip--${c.kind}`}
              title={c.code}
            >
              {c.label}
            </span>
          ))}
          {typeof finding.discipline === 'string' && finding.discipline.trim() !== '' && (
            <span className="finding__discipline">{finding.discipline}</span>
          )}
        </p>
      )}

      {/* The headline, and the only part that is always on screen. */}
      <p className="finding__summary">{finding.summary}</p>

      {hasMore && (
        <details className="finding__more" ref={printRef}>
          <summary>Detail and sources</summary>
          {typeof finding.detail === 'string' && finding.detail.trim() !== '' && (
            <p className="finding__detail">{finding.detail}</p>
          )}
          {citations.length > 0 && (
            <>
              <p className="finding__citelabel muted">
                {/* Named, because "sources" on its own invites the question this answers: these are
                    what the finding was raised AGAINST, not where it was filed. */}
                What it was checked against
              </p>
              <ul className="finding__cites">
                {citations.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </>
          )}
        </details>
      )}

      {/* A resolution is the answer, so it is never hidden behind the disclosure. */}
      {finding.resolution !== undefined &&
        finding.resolution !== null &&
        finding.resolution.trim() !== '' && (
          <p className="finding__resolution">Resolved: {finding.resolution}</p>
        )}
    </div>
  );
}

/**
 * A review finding whose EVERY source document was withdrawn after it was raised.
 *
 * Ruled by Brad Welsh, 2026-10-02: shown marked 'source withdrawn', stays visible, and a person
 * decides. It states what happened and which documents; it does not retire the finding, and nothing
 * here writes.
 */
function SourceWithdrawnNote({ mark }: { mark: SourceWithdrawalView }) {
  const names = mark.withdrawn.map((d) => d.filename ?? d.documentId).join(', ');
  const all =
    mark.sourceCount === 1
      ? 'The one document this finding quoted has'
      : `All ${String(mark.sourceCount)} documents this finding quoted have`;
  return (
    <p className="facts__meta facts__withdrawn" role="note">
      <strong>Source withdrawn</strong> — {all} been withdrawn from the record ({names}). It stays
      here until a person decides what it means now.
    </p>
  );
}

/** True when the claim's own expiry has passed — an unconfirmed claim must not quietly harden. */
function isStale(fact: RecordFactView, now: Date): boolean {
  if (fact.tier === 'confirmed' || !fact.expiresAt) return false;
  return new Date(fact.expiresAt).getTime() < now.getTime();
}

/**
 * The resolution line for one fact.
 *
 * ⚠️ THE STATE COMES FROM THE SERVER. This used to derive `overdue` in the browser as
 * `dueAt && dueAt < now`, which was correct only while a clock was always a date — and every register
 * finding's clock is an EVENT ("DD issue"), so `dueAt` is null on all of them and the client could
 * never show one as overdue no matter how late it was. The server resolves the event against the
 * project's milestone calendar (Phase 3a/3a-ii); duplicating that here would be a second source of
 * truth that silently disagrees.
 *
 * ⚠️ `unresolved` RENDERS AS A QUESTION, NOT AS REASSURANCE. An item nobody can schedule is not an
 * item that is fine, and the reasons name the exact string a human must add to the calendar.
 */
function reasonText(code: string, subject: string | undefined): string {
  // A client's read carries the code without the register text it would quote (canon "Where files
  // go"), so the reason is said without it rather than as “undefined” (Bugbot, #1604).
  if (subject === undefined || subject.trim() === '') {
    if (code === 'unowned-but-expected') return 'nobody is assigned yet';
    if (code === 'milestone-undated') return 'its milestone has no date yet';
    if (code === 'no-milestone-match') return 'its milestone is not on the calendar yet';
    return 'no contact is on file yet';
  }
  if (code === 'unowned-but-expected') return `settles on “${subject}” but nobody is assigned`;
  if (code === 'milestone-undated') return `“${subject}” is on the calendar but has no date`;
  if (code === 'no-milestone-match') return `no milestone matches “${subject}”`;
  return `no contact for “${subject}”`;
}

function ResolutionLine({ fact }: { fact: RecordFactView }) {
  const r = fact.resolution;

  /*
    ⚠️ THE OWNER RENDERS WHETHER OR NOT THERE IS A RESOLUTION, and the first draft of this component
    got that wrong — it returned null without one, which DELETED the owner name from every row on any
    response that did not carry a resolution. The existing component tests caught it.

    The owner is a fact on the row; the resolution is a verdict ABOUT the row. Losing the fact because
    the verdict is missing is the same mistake in miniature as treating `unresolved` as "fine".
  */
  const unresolved = r?.state === 'unresolved';
  const owner = fact.ownerName ?? (unresolved ? 'Unassigned' : null);
  if (!owner) return null;

  // No resolution at all (an older API response) is NOT "unowned" — render the owner, claim nothing
  // about whether it is late.
  if (!r || r.state === 'unowned' || r.state === 'closed') {
    return <p className="facts__owner">{owner}</p>;
  }

  if (unresolved) {
    const reasons = r.reasons ?? [];
    return (
      <p className="facts__owner facts__owner--unresolved">
        {owner} · cannot be dated yet
        {reasons.length > 0 && (
          <span className="facts__reasons muted">
            {' '}
            — {reasons.map((x) => reasonText(x.code, x.subject)).join('; ')}
          </span>
        )}
      </p>
    );
  }

  return (
    <p className={`facts__owner${r.state === 'overdue' ? ' facts__owner--overdue' : ''}`}>
      {owner}
      {r.dueDate ? ` · due ${timestamp(r.dueDate)}` : ''}
      {/* Where the date came from matters: a milestone date moves, a stated due date does not. */}
      {r.dueFrom === 'milestone' ? ' (milestone)' : ''}
      {r.state === 'overdue' ? ' · overdue' : ''}
    </p>
  );
}

/**
 * A count on screen, its list one click away.
 *
 * Both of the record's worklists — milestones that would make items datable, parties that would make
 * them chaseable — are lists of names a person acts on. Inlined into a sentence they are neither
 * readable nor actionable; The Commons rendered nine quoted party names run together as prose.
 *
 * ⚠️ Nothing is hidden that was not already effectively unreadable, and `usePrintExpanded` keeps the
 * whole list in a printed record — a project printed for an IC packet must not lose its worklist
 * behind a chevron.
 */
function Worklist({
  count,
  label,
  items,
}: {
  count: number;
  label: string;
  items: readonly string[];
}) {
  const printRef = usePrintExpanded();
  if (count === 0) return null;
  return (
    <details className="worklist" ref={printRef}>
      <summary>{label}</summary>
      <ul className="worklist__items">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </details>
  );
}

/**
 * What the record still cannot answer, said in one line.
 *
 * ⚠️ A NULL SUMMARY IS NOT A CLEAN ONE. "No answer available" and "nothing unresolved" are opposite
 * facts, and rendering zeroes for the first would assert a clean bill of health nobody gave.
 */
function ResolutionSummaryLine({ summary }: { summary: FactResolutionSummary | null }) {
  // ⚠️ SILENT, not zeroed. With no summary the honest move is to say nothing — rendering
  // "0 overdue · 0 owed" would assert a clean bill of health nobody gave. Silence claims nothing, and
  // the per-fact lines still render each owner without asserting a state.
  if (!summary) return null;

  const { owed, overdue, unresolved, unmatchedEvents, unmatchedParties } = summary;
  if (owed + overdue + unresolved === 0) return null;

  return (
    <>
      <p className="card__note">
        <strong>{overdue}</strong> overdue · <strong>{owed}</strong> owed ·{' '}
        <strong>{unresolved}</strong> not yet datable
      </p>

      {/*
        ⚠️ THE COUNT IS THE SIGNAL; THE LIST IS THE WORK. Both worklists used to inline every entry
        into the sentence — on The Commons that is nine quoted party names and a milestone list run
        together as prose, which is unreadable as a sentence AND unusable as a to-do list. The counts
        stay on screen; the names become an actual list, one click away.
      */}
      <Worklist
        count={unmatchedEvents.length}
        label={`Add ${unmatchedEvents.length} milestone${unmatchedEvents.length === 1 ? '' : 's'} so these can be dated`}
        items={unmatchedEvents}
      />
      {/*
        ⚠️ The DIRECTORY worklist (Phase 3b). Rendered separately from the milestone worklist, and
        worded as a different job, because it answers a different question: a milestone makes an item
        DATABLE, a contact makes it CHASEABLE. Merging them into one "things to add" line would imply
        that supplying either fixes the same problem.

        This line is also what retires the `unmatchedParties` orphan-field exemption — it was allowed
        to go unread only while the directory did not exist.
      */}
      <Worklist
        count={unmatchedParties.length}
        label={`${unmatchedParties.length} part${unmatchedParties.length === 1 ? 'y has' : 'ies have'} no directory contact, so nobody can be notified`}
        items={unmatchedParties}
      />
    </>
  );
}

/**
 * The Open Items Register, as one line of counts (ADR-0073).
 *
 * ⚠️ COUNTS, NOT ROWS — and deliberately. The register is ~150 items; listed here it would be nine rows
 * in ten of this panel and bury the restrictions and design findings. Its own surface is ADR-0073 step
 * 3. Until then this line is how a reader learns the register is on the record and what it owes.
 *
 * ⚠️ `null` RENDERS NOTHING, and zero renders nothing — but for different reasons: `null` is "the server
 * did not say", and a zero total is "no register imported", which the claims note below already covers.
 * Neither may render as "0 open", which would assert a clean register nobody has read.
 */
function OpenItemsLine({ openItems }: { openItems: OpenItemsSummaryView | null }) {
  if (!openItems || openItems.total === 0) return null;
  const { total, answered, resolution } = openItems;
  const closed = resolution.closed ?? 0;
  const { overdue, owed, unresolved } = resolution;
  return (
    <p className="card__note">
      Open Items Register: <strong>{total - closed}</strong> open of {total}
      {answered > 0 ? ` (${answered} answered, awaiting closure)` : ''} · <strong>{overdue}</strong>{' '}
      overdue · <strong>{owed}</strong> owed · <strong>{unresolved}</strong> not yet datable ·{' '}
      {closed} closed
    </p>
  );
}

/**
 * How many claims can sit open before the list stops being scannable.
 *
 * Twelve, because the real case is 21 and the panel is read beside a variance list of similar length.
 * Not a round number chosen for tidiness — it is under the measured case and above the handful a
 * restriction-bearing record carries.
 */
const COLLAPSE_ABOVE = 12;

/**
 * The claims list, collapsed when it is long enough to be the problem and plain when it is not.
 *
 * ⚠️ ONE `<ul>` EITHER WAY, so the rows are identical in both states and nothing about a claim depends
 * on how many siblings it has. `<details>` rather than React state: find-in-page reaches it, the
 * keyboard and screen-reader contracts come for free, and it prints expandable — which a governed
 * record has to be.
 */
function FactList({
  collapsed,
  count,
  children,
}: {
  collapsed: boolean;
  count: number;
  children: ReactNode;
}) {
  /*
    ⚠️ THE PRINT REF IS NOT OPTIONAL, and a guard enforces it. A closed `<details>` hides its content
    through the user agent's own shadow slot, so a print stylesheet has nothing to override — a record
    printed for an IC packet would silently lose every claim. `usePrintExpanded.test.ts` caught this
    the first time this component rendered a disclosure without one.
  */
  const printRef = usePrintExpanded();
  const list = <ul className="facts">{children}</ul>;
  if (!collapsed) return list;
  return (
    <details className="worklist" ref={printRef}>
      <summary>{count} claims on this record. Open to read them.</summary>
      {list}
    </details>
  );
}

/**
 * A finding that PROPOSES a protected stop (ADR-0063), and — for an owner — the decision on it.
 *
 * 🔴 UNTIL THIS EXISTED, THE DECISION HAD NO DOOR IN THE PRODUCT. The attest route was built, tested and
 * deployed, and an owner could confirm a life-safety stop only with a hand-made HTTP call; a proposed
 * candidate rendered exactly like any other finding. The label makes the proposal visible to everyone;
 * the controls make it decidable by the one role that may decide it.
 *
 * ⚠️ The decision is made on THE ROW ON SCREEN: `fact.id` goes with it, and the server refuses (stale)
 * if the finding changed since this page loaded, rather than applying the decision to text nobody read.
 */
function ProtectedCandidate({
  fact,
  label,
  canAttest,
  onAttest,
}: {
  fact: RecordFactView;
  label: string;
  canAttest: boolean;
  onAttest?: (
    fact: RecordFactView,
    outcome: 'confirm' | 'reject',
    rationale: string,
  ) => Promise<AttestResult>;
}) {
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (fact.tier === 'confirmed') {
    return (
      <p className="facts__candidate facts__candidate--confirmed">
        <strong>Protected stop confirmed — {label}.</strong> An owner attested this finding and a
        Stop &amp; Fix is open against it.
      </p>
    );
  }

  const decide = async (outcome: 'confirm' | 'reject') => {
    if (!onAttest) return;
    setBusy(true);
    setMessage(null);
    /*
      ⚠️ `finally`, not a line after the await (Bugbot, #1227). A network failure makes `fetch` THROW
      rather than return a result, and the controls then stayed disabled with no message until a full
      reload — the one decision on the record a person could not retry.
    */
    try {
      const r = await onAttest(fact, outcome, rationale);
      if (r.kind === 'stale') {
        setMessage(
          'This finding changed since the page loaded. Reload and decide on the current text.',
        );
      } else if (r.kind === 'refused') {
        setMessage(r.message);
      }
    } catch {
      setMessage('We couldn’t reach the server — nothing was recorded. Give it another try.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="facts__candidate">
      <p>
        <strong>Proposed protected stop — {label}.</strong> Nothing is blocked until an owner
        confirms it; a confirmation opens a Stop &amp; Fix that holds the next release.
      </p>
      {canAttest && onAttest ? (
        <div className="facts__attest">
          <label className="facts__attest-note">
            Your reasoning (recorded with the decision)
            <textarea
              value={rationale}
              maxLength={4000}
              rows={2}
              onChange={(e) => setRationale(e.target.value)}
            />
          </label>
          <div className="facts__attest-actions">
            <button
              type="button"
              className="facts__attest-btn facts__attest-btn--confirm"
              disabled={busy}
              onClick={() => void decide('confirm')}
            >
              Confirm stop
            </button>
            <button
              type="button"
              className="facts__attest-btn"
              disabled={busy}
              onClick={() => void decide('reject')}
            >
              Reject
            </button>
          </div>
          {message && <p className="facts__attest-message">{message}</p>}
        </div>
      ) : (
        <p className="muted">Awaiting an owner’s decision.</p>
      )}
    </div>
  );
}

export function RecordFacts({
  facts,
  resolution = null,
  now = new Date(),
  canAttest = false,
  onAttest,
  openItems = null,
  reviewCoverage = null,
  findingsWithheld = null,
}: {
  facts: RecordFactView[];
  /**
   * Which protected questions the technical review asked (#1379). Shown above the claims, beside the
   * candidates it explains: no candidate from a question nobody asked is not a clean result.
   */
  reviewCoverage?: ReviewCoverageReadView | null;
  /**
   * Design-review findings held back from this reader because their wording touches GGP's pricing
   * (canon "Where files go"). Counted out loud, never silently absent; `null` = not said.
   */
  findingsWithheld?: number | null;
  /** The Open Items Register's counts (ADR-0073). `null` = not said; rendered as nothing. */
  openItems?: OpenItemsSummaryView | null;
  /** The server's summary. `null` means "no answer", which is rendered as such — never as zeroes. */
  resolution?: FactResolutionSummary | null;
  /** Injected so the stale rendering is testable without freezing the clock globally. */
  now?: Date;
  /** May this caller decide a protected-stop candidate (`stop:attest`, owner only — ADR-0063 Q2)? */
  canAttest?: boolean;
  /** Record an owner's decision on a candidate. Absent ⇒ no controls are rendered at all. */
  onAttest?: (
    fact: RecordFactView,
    outcome: 'confirm' | 'reject',
    rationale: string,
  ) => Promise<AttestResult>;
}) {
  return (
    <section className="card">
      <h3 className="card__title">
        What this record claims
        {facts.length > 0 && <span className="pill pill--badge">{facts.length}</span>}
      </h3>

      <ReviewCoverage coverage={reviewCoverage} />
      {facts.length > 0 && <ResolutionSummaryLine summary={resolution} />}
      <OpenItemsLine openItems={openItems} />
      {findingsWithheld !== null && findingsWithheld > 0 && (
        <p className="card__note">
          {findingsWithheld === 1
            ? '1 more design-review item is not shown here.'
            : `${findingsWithheld} more design-review items are not shown here.`}
        </p>
      )}

      {/*
        ── A LONG CLAIMS LIST COLLAPSES, AND SAYS WHAT IS INSIDE IT ─────────────────────────────────

        🔴 MEASURED: The Commons carries 21 current claims, every one a register finding, and they sat
        open beside 27 variances — about 48 multi-line rows in one panel. The row DETAIL already
        collapses (Brad, 2026-08-27: "This is a mess"); what was missing was a group level.

        ⚠️ THE THRESHOLD IS NOT COSMETIC. Below it the list is shorter than the disclosure that would
        hide it, and collapsing three claims makes a reader click to learn there was nothing to click
        for. Above it the list stops being scannable, which is the actual complaint.

        ⚠️ NOTHING IS HIDDEN WITHOUT A SIGNAL. The summary carries the count and the resolution line
        stays OUTSIDE the disclosure — what is owed, overdue and unresolved is the thing a reader came
        for, and it must not be one click away.
      */}
      {facts.length === 0 ? (
        // Said in words. A blank panel reads as a rendering fault, and "no claims recorded" is a real
        // and different state from "this record has no restrictions".
        <p className="card__note">
          {openItems && openItems.total > 0
            ? 'No restrictions or design-register findings are recorded.'
            : 'Nothing has been recorded about this project yet — no restrictions, no register findings.'}
        </p>
      ) : (
        <FactList collapsed={facts.length > COLLAPSE_ABOVE} count={facts.length}>
          {facts.map((f) => {
            const stale = isStale(f, now);
            return (
              <li key={f.id} className="facts__row">
                <div className="facts__head">
                  <span className={`facts__tier facts__tier--${TIER_COPY[f.tier].tone}`}>
                    {TIER_COPY[f.tier].label}
                  </span>
                  <span className="facts__key">
                    {KIND_LABELS[f.kind] ?? f.kind}: {f.key}
                  </span>
                  {/* A finding's value is paragraphs, not a value — it renders BELOW the head rather
                      than squeezed into it. Everything else keeps the one-line treatment. */}
                  {!isRegisterFinding(f.value) && (
                    <span className="facts__value">{factValue(f.value)}</span>
                  )}
                </div>

                {f.sourceWithdrawal && <SourceWithdrawnNote mark={f.sourceWithdrawal} />}

                {isRegisterFinding(f.value) && <FindingBody finding={f.value} />}

                {(() => {
                  // A3 S5: a closure the design review PROPOSES. Outside any disclosure, so it prints.
                  const note = closureCandidateNote(f.value, f.source);
                  return note ? (
                    <p className="facts__meta facts__closure" role="note">
                      <strong>Proposed by design review:</strong> {note}
                    </p>
                  ) : null;
                })()}

                {(() => {
                  const candidate = protectedCandidateOf(f.value);
                  return candidate ? (
                    <ProtectedCandidate
                      fact={f}
                      label={candidate.label}
                      canAttest={canAttest}
                      {...(onAttest ? { onAttest } : {})}
                    />
                  ) : null;
                })()}

                {/* Who said it, and how it got here — the two provenance questions the ruling named. */}
                <p className="facts__meta muted">
                  Stated by {f.source} · via {f.arrivalPath}
                  {f.recordedByName ? ` · recorded by ${f.recordedByName}` : ''} ·{' '}
                  {timestamp(f.recordedAt)}
                </p>

                {/* What would settle it. A claim with no settling event is one nothing will ever
                    confirm, and saying so is more useful than leaving the line out. */}
                {f.tier !== 'confirmed' && (
                  <p className="facts__meta">
                    {f.settlesOn
                      ? `Settles on: ${f.settlesOn}.`
                      : 'No settling event recorded — nothing will confirm this on its own.'}
                    {f.expiresAt &&
                      (stale
                        ? ` This claim expired ${timestamp(f.expiresAt)} and has not been confirmed.`
                        : ` Expires ${timestamp(f.expiresAt)}.`)}
                  </p>
                )}

                {/*
                  Ruling 4: an owned item carries a person AND a clock — and an overdue item says so,
                  because that is the whole reason the clock is on the row.

                  ⚠️ THE VERDICT IS THE SERVER'S NOW (Phase 3a). This block used to compute overdue
                  here as `dueAt && dueAt < now`, which was right only while a clock was always a
                  date. Migration 0034 widened the mechanism because the register the ruling was made
                  about has neither a platform user nor a timestamp — its owners are outside firms and
                  its clocks are drawing issues — so `dueAt` is null on every register finding and
                  this could never mark one overdue however late it ran. The server resolves the event
                  against the project's milestone calendar; see `ResolutionLine`.
                */}
                <ResolutionLine fact={f} />
              </li>
            );
          })}
        </FactList>
      )}
    </section>
  );
}
